import { CommandProps, Editor, RawCommands } from '@tiptap/react';
import { LinkOptions } from '@tiptap/extension-link';
import {
  openPresentationSelectionDialog,
  PresentationDialogLabels,
  PresentationModalOption,
} from './PresentationSelectorDialog';
import { Link, selectWholeLink } from './LinkExtension';
import { getTextFromSelection } from './extensionUtils';

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    presentation: {
      insertPresentation: (presentation: PresentationItem) => ReturnType;
      removePresentation: () => ReturnType;
    };
  }
}

declare module '@tiptap/core' {
  interface Storage {
    presentation: {
      items: PresentationModalOption[];
      labels?: PresentationDialogLabels;
    };
  }
}

type PresentationOptions = LinkOptions & {
  items: PresentationItem[];
  labels?: PresentationDialogLabels;
};

type PresentationItem = Pick<PresentationModalOption, 'url' | 'label'>;

const PresentationExtension = Link.extend<PresentationOptions>({
  name: 'presentation',

  addOptions() {
    return {
      ...(this.parent?.() ?? ({} as PresentationOptions)),
      items: [],
      HTMLAttributes: {
        'data-type': 'presentation',
      },
    };
  },

  addStorage() {
    return {
      items: [],
    };
  },

  onCreate(event) {
    this.parent?.(event);
    this.storage.items = this.options.items.map(item => ({
      url: item.url,
      label: item.label,
      value: item.label,
    }));
    this.storage.labels = this.options.labels;
  },

  parseHTML() {
    return [
      {
        tag: 'a[href*="ardoq.com/presentation"]',
      },
      {
        tag: 'a[href*="ardoq.dev/presentation"]',
      },
    ];
  },

  addCommands() {
    return {
      insertPresentation:
        (presentation: PresentationItem) =>
        ({ chain }: CommandProps) => {
          return chain()
            .focus()
            .insertContent({
              type: 'text',
              text: presentation.label,
              marks: [
                {
                  type: 'presentation',
                  attrs: {
                    href: presentation.url,
                    'data-type': 'presentation',
                    target: '_blank',
                  },
                },
              ],
            })
            .run();
        },
      removePresentation:
        () =>
        ({ chain }: CommandProps) => {
          return chain()
            .focus()
            .extendMarkRange('presentation')
            .deleteSelection()
            .run();
        },
    } as Partial<RawCommands>;
  },
});

const resolveHrefAndText = (editor: Editor) => {
  if (editor.isActive('presentation')) {
    const { href } = editor.getAttributes('presentation');

    const { from: pos } = editor.state.selection;
    selectWholeLink('presentation', editor.view, pos);

    return { href, text: getTextFromSelection(editor) };
  }

  return { href: '', text: undefined };
};

export const handlePresentationSelection = async (editor: Editor) => {
  const { href, text } = resolveHrefAndText(editor);
  const items =
    (editor.extensionStorage.presentation.items as PresentationModalOption[]) ||
    [];
  const labels = editor.extensionStorage.presentation.labels as
    | PresentationDialogLabels
    | undefined;

  const result = await openPresentationSelectionDialog({
    url: href,
    options: items,
    linkText: text,
    labels,
    onRemove: () => {
      editor.commands.removePresentation();
    },
    onInsert: ({ linkText, url }) => {
      editor.commands.insertPresentation({
        url,
        label: linkText,
      });
    },
    onUpdate: ({ linkText, url }) => {
      editor.commands.removePresentation();
      editor.commands.insertPresentation({
        url,
        label: linkText,
      });
    },
  });

  if (!result) return;

  editor.commands.insertPresentation({
    url: result.url,
    label: result.newLinkText,
  });
};

export { PresentationExtension };
